import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DataTable from '../components/DataTable';
import { ArrowLeft, Edit, Users, Bell } from 'lucide-react';
import SignalChart from '../components/SignalChart';
import SyncStatus from '../components/SyncStatus';
const AccountDetail: React.FC = () => {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'users' | 'signals'>('users');
  // Mock account data
  const account = {
    id: parseInt(id || '1'),
    name: 'Acme Corporation',
    industry: 'Technology',
    website: 'https://acme.com',
    location: 'San Francisco, CA',
    totalUsers: 125,
    mrr: '$12,500',
    status: 'Active',
    createdAt: 'Jan 15, 2023',
    lastActivity: '2 hours ago',
    description: 'Acme Corporation is a leading technology company specializing in cloud solutions and enterprise software.',
    contactName: 'John Smith',
    contactEmail: 'john.smith@acme.com',
    contactPhone: '(555) 123-4567',
    healthScore: 85
  };
  // Mock users data
  const userColumns = [{
    key: 'name',
    label: 'Name',
    sortable: true
  }, {
    key: 'email',
    label: 'Email',
    sortable: true
  }, {
    key: 'role',
    label: 'Role',
    sortable: true
  }, {
    key: 'lastActive',
    label: 'Last Active',
    sortable: true
  }, {
    key: 'sessions',
    label: 'Sessions',
    sortable: true
  }, {
    key: 'status',
    label: 'HubSpot Sync',
    sortable: true,
    render: (value: string) => <SyncStatus status={value as 'Synced' | 'Not Synced'} data-id="element-417" />
  }];
  const userData = [{
    id: 1,
    name: 'John Smith',
    email: 'john.smith@acme.com',
    role: 'Admin',
    lastActive: '2 hours ago',
    sessions: 128,
    status: 'Synced'
  }, {
    id: 2,
    name: 'Sarah Johnson',
    email: 'sarah.j@acme.com',
    role: 'User',
    lastActive: '1 day ago',
    sessions: 56,
    status: 'Synced'
  }, {
    id: 3,
    name: 'Michael Brown',
    email: 'm.brown@acme.com',
    role: 'Admin',
    lastActive: '3 hours ago',
    sessions: 87,
    status: 'Synced'
  }, {
    id: 4,
    name: 'Emily Davis',
    email: 'e.davis@acme.com',
    role: 'User',
    lastActive: '3 days ago',
    sessions: 23,
    status: 'Not Synced'
  }, {
    id: 5,
    name: 'David Wilson',
    email: 'd.wilson@acme.com',
    role: 'Manager',
    lastActive: '5 hours ago',
    sessions: 64,
    status: 'Synced'
  }];
  // Updated signal chart data with realistic time-based bar and line charts
  const signalChartData = [{
    id: 1,
    title: 'Login Activity',
    type: 'bar',
    subtitle: 'Daily Logins',
    actionType: 'Authentication',
    timeSeriesData: [{
      date: 'Mon',
      value: 42
    }, {
      date: 'Tue',
      value: 58
    }, {
      date: 'Wed',
      value: 65
    }, {
      date: 'Thu',
      value: 78
    }, {
      date: 'Fri',
      value: 52
    }, {
      date: 'Sat',
      value: 28
    }, {
      date: 'Sun',
      value: 22
    }],
    trend: 'up',
    percentage: 15,
    timeframe: 'vs last week'
  }, {
    id: 2,
    title: 'Feature Adoption',
    type: 'line',
    subtitle: 'New Features Used',
    actionType: 'User Action',
    timeSeriesData: [{
      date: 'Jan',
      value: 5
    }, {
      date: 'Feb',
      value: 8
    }, {
      date: 'Mar',
      value: 12
    }, {
      date: 'Apr',
      value: 15
    }, {
      date: 'May',
      value: 18
    }, {
      date: 'Jun',
      value: 22
    }, {
      date: 'Jul',
      value: 25
    }, {
      date: 'Aug',
      value: 31
    }, {
      date: 'Sep',
      value: 38
    }],
    trend: 'up',
    percentage: 25,
    timeframe: 'vs last 6 months'
  }, {
    id: 3,
    title: 'Report Creation',
    type: 'bar',
    subtitle: 'Reports Generated',
    actionType: 'User Action',
    timeSeriesData: [{
      date: 'Week 1',
      value: 12
    }, {
      date: 'Week 2',
      value: 18
    }, {
      date: 'Week 3',
      value: 15
    }, {
      date: 'Week 4',
      value: 22
    }, {
      date: 'Week 5',
      value: 27
    }, {
      date: 'Week 6',
      value: 24
    }],
    trend: 'up',
    percentage: 12,
    timeframe: 'vs last month'
  }, {
    id: 4,
    title: 'User Onboarding',
    type: 'line',
    subtitle: 'Completion Rate',
    actionType: 'User Flow',
    timeSeriesData: [{
      date: 'Step 1',
      value: 100
    }, {
      date: 'Step 2',
      value: 92
    }, {
      date: 'Step 3',
      value: 85
    }, {
      date: 'Step 4',
      value: 78
    }, {
      date: 'Step 5',
      value: 65
    }, {
      date: 'Step 6',
      value: 55
    }],
    trend: 'down',
    percentage: 5,
    timeframe: 'vs last cohort'
  }, {
    id: 5,
    title: 'Error Occurrences',
    type: 'bar',
    subtitle: 'By Error Type',
    actionType: 'System Event',
    timeSeriesData: [{
      date: 'API',
      value: 12
    }, {
      date: 'Auth',
      value: 5
    }, {
      date: 'UI',
      value: 8
    }, {
      date: 'Data',
      value: 15
    }, {
      date: 'Other',
      value: 3
    }],
    trend: 'down',
    percentage: 10,
    timeframe: 'vs last month'
  }, {
    id: 6,
    title: 'Feature Requests',
    type: 'bar',
    subtitle: 'Most Requested',
    actionType: 'User Feedback',
    timeSeriesData: [{
      date: 'Reports',
      value: 28
    }, {
      date: 'Dashboard',
      value: 22
    }, {
      date: 'Alerts',
      value: 15
    }, {
      date: 'Analytics',
      value: 12
    }, {
      date: 'Mobile App',
      value: 8
    }, {
      date: 'Integrations',
      value: 18
    }],
    trend: 'up',
    percentage: 20,
    timeframe: 'vs last quarter'
  }];
  const handleUserClick = (user: any) => {
    navigate(`/users/${user.id}`);
  };
  return <div className="space-y-6" data-id="element-418">
      <div className="flex items-center gap-4" data-id="element-419">
        <button onClick={() => navigate('/accounts')} className="p-2 rounded-full hover:bg-gray-100" data-id="element-420">
          <ArrowLeft size={20} data-id="element-421" />
        </button>
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-422">{account.name}</h1>
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${account.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-423">
          {account.status}
        </span>
        <button className="ml-auto bg-[#0070d2] hover:bg-[#0057a3] text-white px-4 py-2 rounded-md flex items-center gap-2" data-id="element-424">
          <Edit size={16} data-id="element-425" />
          <span data-id="element-426">Edit Account</span>
        </button>
      </div>
      <div className="grid grid-cols-3 gap-6" data-id="element-427">
        <div className="col-span-1 bg-white border border-gray-200 rounded-md shadow-sm" data-id="element-428">
          <div className="p-6" data-id="element-429">
            <h2 className="text-lg font-medium text-gray-900 mb-4" data-id="element-430">
              Account Information
            </h2>
            <div className="space-y-4" data-id="element-431">
              <div data-id="element-432">
                <h3 className="text-sm font-medium text-gray-500" data-id="element-433">Industry</h3>
                <p className="mt-1 text-sm text-gray-900" data-id="element-434">{account.industry}</p>
              </div>
              <div data-id="element-435">
                <h3 className="text-sm font-medium text-gray-500" data-id="element-436">Website</h3>
                <p className="mt-1 text-sm text-blue-600" data-id="element-437">{account.website}</p>
              </div>
              <div data-id="element-438">
                <h3 className="text-sm font-medium text-gray-500" data-id="element-439">Location</h3>
                <p className="mt-1 text-sm text-gray-900" data-id="element-440">{account.location}</p>
              </div>
              <div data-id="element-441">
                <h3 className="text-sm font-medium text-gray-500" data-id="element-442">
                  Total Users
                </h3>
                <p className="mt-1 text-sm text-gray-900" data-id="element-443">
                  {account.totalUsers}
                </p>
              </div>
              <div data-id="element-444">
                <h3 className="text-sm font-medium text-gray-500" data-id="element-445">MRR</h3>
                <p className="mt-1 text-sm text-gray-900" data-id="element-446">{account.mrr}</p>
              </div>
              <div data-id="element-447">
                <h3 className="text-sm font-medium text-gray-500" data-id="element-448">Created</h3>
                <p className="mt-1 text-sm text-gray-900" data-id="element-449">
                  {account.createdAt}
                </p>
              </div>
              <div data-id="element-450">
                <h3 className="text-sm font-medium text-gray-500" data-id="element-451">
                  Last Activity
                </h3>
                <p className="mt-1 text-sm text-gray-900" data-id="element-452">
                  {account.lastActivity}
                </p>
              </div>
              <div data-id="element-453">
                <h3 className="text-sm font-medium text-gray-500" data-id="element-454">
                  Health Score
                </h3>
                <div className="mt-1 flex items-center" data-id="element-455">
                  <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-456">
                    <div className="bg-green-500 h-2.5 rounded-full" style={{
                    width: `${account.healthScore}%`
                  }} data-id="element-457"></div>
                  </div>
                  <span className="ml-2 text-sm text-gray-900" data-id="element-458">
                    {account.healthScore}%
                  </span>
                </div>
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-gray-200" data-id="element-459">
              <h3 className="text-sm font-medium text-gray-500" data-id="element-460">
                Primary Contact
              </h3>
              <div className="mt-2" data-id="element-461">
                <p className="text-sm font-medium text-gray-900" data-id="element-462">
                  {account.contactName}
                </p>
                <p className="text-sm text-gray-600" data-id="element-463">{account.contactEmail}</p>
                <p className="text-sm text-gray-600" data-id="element-464">{account.contactPhone}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="col-span-2" data-id="element-465">
          <div className="bg-white border border-gray-200 rounded-md shadow-sm" data-id="element-466">
            <div className="border-b border-gray-200" data-id="element-467">
              <nav className="flex -mb-px" data-id="element-468">
                <button className={`py-4 px-6 text-sm font-medium border-b-2 ${activeTab === 'users' ? 'border-[#0070d2] text-[#0070d2]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('users')} data-id="element-469">
                  <div className="flex items-center gap-2" data-id="element-470">
                    <Users size={18} data-id="element-471" />
                    <span data-id="element-472">Users</span>
                  </div>
                </button>
                <button className={`py-4 px-6 text-sm font-medium border-b-2 ${activeTab === 'signals' ? 'border-[#0070d2] text-[#0070d2]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('signals')} data-id="element-473">
                  <div className="flex items-center gap-2" data-id="element-474">
                    <Bell size={18} data-id="element-475" />
                    <span data-id="element-476">Actions</span>
                  </div>
                </button>
              </nav>
            </div>
            {activeTab === 'users' ? <DataTable columns={userColumns} data={userData} onRowClick={handleUserClick} data-id="element-477" /> : <div className="p-6" data-id="element-478">
                <div className="grid grid-cols-2 gap-6" data-id="element-479">
                  {signalChartData.map(chart => <SignalChart key={chart.id} data={chart} data-id="element-480" />)}
                </div>
              </div>}
          </div>
        </div>
      </div>
    </div>;
};
export default AccountDetail;